/*
 * AdminVideosPanel — gerencia o vídeo de comunicação (fullscreen bloqueante)
 * exibido a todos os funcionários no login. Só UM vídeo ativo por vez.
 * Fluxo idêntico ao AdminBannersPanel, mas com upload de MP4/WebM/MOV
 * (até 200 MB) e streaming via Range.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Video as VideoIcon, Upload, Loader2, Trash2, ToggleLeft, ToggleRight,
  Users, CheckCircle2, CalendarClock, StopCircle, History, Film,
} from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const STATUS_META = {
  running:   { label: "No ar",     className: "bg-emerald-500 text-white" },
  scheduled: { label: "Agendado",  className: "bg-sky-500 text-white" },
  paused:    { label: "Pausado",   className: "bg-slate-400 text-white" },
  expired:   { label: "Vencido",   className: "bg-amber-500 text-white" },
  ended:     { label: "Encerrado", className: "bg-slate-600 text-white" },
};

const MAX_MB = 200;

function UploadDialog({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTitle(""); setDescription("");
      setStartsAt(""); setEndsAt("");
      setFile(null);
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null); setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Arquivo acima de ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(f ? URL.createObjectURL(f) : null);
  };

  const submit = async () => {
    if (!file) return toast.error("Selecione um vídeo.");
    if (startsAt && endsAt && endsAt <= startsAt) {
      return toast.error("A data final precisa ser depois da inicial.");
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      fd.append("active", "true");
      if (startsAt) fd.append("starts_at", startsAt);
      if (endsAt) fd.append("ends_at", endsAt);
      await axios.post(`${API}/admin/videos/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 5 * 60 * 1000,
      });
      toast.success("Vídeo publicado.");
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"
                      data-testid="video-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-indigo-800">
            <Film className="w-5 h-5" />
            Publicar Vídeo de Comunicação
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-700">Título (opcional)</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="Ex: Boas-vindas AVI 2026" className="mt-1"
                   data-testid="video-title" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Descrição interna (opcional)</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                       rows={2} className="mt-1 text-sm"
                       placeholder="Contexto interno — não aparece pro funcionário"
                       data-testid="video-description" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <CalendarClock className="w-3 h-3" /> Início (opcional)
              </label>
              <Input type="datetime-local" value={startsAt}
                      onChange={(e) => setStartsAt(e.target.value)}
                      className="mt-1 text-xs"
                      data-testid="video-starts-at" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <CalendarClock className="w-3 h-3" /> Encerra em (opcional)
              </label>
              <Input type="datetime-local" value={endsAt}
                      onChange={(e) => setEndsAt(e.target.value)}
                      className="mt-1 text-xs"
                      data-testid="video-ends-at" />
            </div>
          </div>
          <p className="text-[10px] text-slate-500">
            Se preencher as datas, o vídeo só aparece pra funcionários dentro
            desse período (fuso Brasília). Vazio = fica no ar até você desativar.
          </p>
          <div>
            <label className="text-xs font-semibold text-slate-700">
              Vídeo (MP4, WebM ou MOV, máx {MAX_MB} MB) *
            </label>
            <Input ref={fileRef} type="file"
                    accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                    onChange={onFile} className="mt-1 text-xs"
                    data-testid="video-file" />
            {preview && (
              <div className="mt-2 rounded-lg overflow-hidden border border-slate-200 bg-slate-900 p-1">
                <video src={preview} controls playsInline
                        className="w-full max-h-72" />
                <p className="text-[10px] text-slate-400 mt-1 text-center">
                  Prévia local (será enviada ao clicar em Publicar)
                </p>
              </div>
            )}
          </div>
          <p className="text-[11px] text-slate-500">
            📌 Ao publicar, o vídeo anterior é <b>desativado automaticamente</b> — só existe UM vídeo ativo por vez. O funcionário verá o vídeo em tela cheia no login e precisa <b>assistir 100%</b> antes de dar Ciente.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || !file}
                   className="bg-indigo-600 hover:bg-indigo-700 text-white"
                   data-testid="video-submit">
            {busy
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Publicando…</>
              : <><Upload className="w-4 h-4 mr-2" />Publicar</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AcksDialog({ open, video, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!video) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/videos/${video.id}/acknowledgments`);
      setItems(r.data?.items || []);
    } catch { toast.error("Falha ao carregar."); }
    finally { setLoading(false); }
  }, [video]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto"
                      data-testid="video-acks-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            Cientes — {video?.title || "Vídeo"}
            <Badge variant="outline">{items.length}</Badge>
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-center text-sm text-slate-500 py-10">
            Ninguém deu Ciente ainda.
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id}
                    className="flex items-center gap-3 p-2.5 rounded bg-slate-50 border border-slate-200"
                    data-testid={`video-ack-item-${it.id}`}>
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {it.employee_nome} {it.employee_matricula
                      ? <span className="font-mono text-[11px] text-slate-500">
                          • {it.employee_matricula}
                        </span>
                      : null}
                  </p>
                  <p className="text-[11px] text-slate-500 font-mono">
                    {fmtDate(it.acknowledged_at)} {it.ip
                      ? <span className="ml-1">• {it.ip}</span>
                      : null}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminVideosPanel() {
  const [items, setItems] = useState([]);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [acksVideo, setAcksVideo] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [endingId, setEndingId] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/videos/list`);
      setItems(r.data?.items || []);
      setTotalEmployees(r.data?.total_employees || 0);
    } catch { toast.error("Falha ao carregar vídeos."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (v) => {
    setTogglingId(v.id);
    try {
      await axios.patch(`${API}/admin/videos/${v.id}`,
        { active: !v.active }, { timeout: 20000 });
      toast.success(!v.active ? "Vídeo ativado." : "Vídeo pausado.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha.");
    } finally { setTogglingId(null); }
  };

  const endCampaign = async (v) => {
    if (!window.confirm(
      `Encerrar o vídeo "${v.title || v.filename}"?\n\n`
      + `Ele para de aparecer, mas o histórico com ${v.ack_count} `
      + `Ciente(s) fica registrado.`,
    )) return;
    setEndingId(v.id);
    try {
      await axios.post(`${API}/admin/videos/${v.id}/end`,
        {}, { timeout: 20000 });
      toast.success("Vídeo encerrado.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao encerrar.");
    } finally { setEndingId(null); }
  };

  const remove = async (v) => {
    if (!window.confirm(
      `Excluir DEFINITIVAMENTE este vídeo?\n\n`
      + `Os ${v.ack_count} "cientes" registrados também serão apagados.`,
    )) return;
    setDeletingId(v.id);
    try {
      await axios.delete(`${API}/admin/videos/${v.id}`, { timeout: 20000 });
      toast.success("Vídeo excluído.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir.");
    } finally { setDeletingId(null); }
  };

  const isActive = (v) =>
    ["running", "scheduled"].includes(v.computed_status);
  const active = items.filter(isActive);
  const history = items.filter((v) => !isActive(v));

  const renderItem = (v) => {
    const meta = STATUS_META[v.computed_status] || STATUS_META.paused;
    const inHistory = !isActive(v);
    const isPreviewing = previewId === v.id;
    return (
      <div key={v.id}
            className={`flex flex-col p-3 rounded-lg border ${
              v.computed_status === "running"
                ? "bg-indigo-50 border-indigo-200"
                : "bg-slate-50 border-slate-200"
            }`}
            data-testid={`video-item-${v.id}`}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-20 h-14 shrink-0 bg-slate-900 rounded border border-slate-200 flex items-center justify-center">
            <VideoIcon className="w-6 h-6 text-slate-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-800 truncate">
                {v.title || v.filename}
              </span>
              <Badge className={`${meta.className} text-[10px]`}>
                {meta.label}
              </Badge>
              <Badge variant="outline" className="text-[10px] font-mono">
                {(v.size_bytes / (1024 * 1024)).toFixed(1)} MB
              </Badge>
            </div>
            {v.description && (
              <p className="text-xs text-slate-600 mt-0.5 truncate">
                {v.description}
              </p>
            )}
            <div className="text-[10px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 font-mono">
              <span>Publicado: {fmtDate(v.created_at)}</span>
              {v.starts_at && <span>Início: {fmtDate(v.starts_at)}</span>}
              {v.ends_at && <span>Fim: {fmtDate(v.ends_at)}</span>}
              {v.ended_at && (
                <span className="text-slate-700">
                  Encerrado: {fmtDate(v.ended_at)}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button size="sm" variant="outline"
                     onClick={() => setPreviewId(isPreviewing ? null : v.id)}
                     data-testid={`video-preview-${v.id}`}
                     className="h-8">
              <VideoIcon className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">
                {isPreviewing ? "Fechar" : "Ver"}
              </span>
            </Button>
            <Button size="sm" variant="outline"
                    onClick={() => setAcksVideo(v)}
                    data-testid={`video-acks-${v.id}`}
                    className="h-8 border-indigo-200 text-indigo-700 hover:bg-indigo-50">
              <Users className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">
                {v.ack_count} Ciente(s) · {v.ack_percent}%
              </span>
            </Button>
            {!inHistory && (
              <>
                <Button size="sm" variant="outline"
                        onClick={() => toggle(v)}
                        disabled={togglingId === v.id}
                        data-testid={`video-toggle-${v.id}`}
                        className="h-8"
                        title={v.active ? "Pausar" : "Reativar"}>
                  {togglingId === v.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : v.active
                      ? <ToggleRight className="w-5 h-5 text-emerald-600" />
                      : <ToggleLeft className="w-5 h-5 text-slate-400" />}
                </Button>
                <Button size="sm" variant="outline"
                        onClick={() => endCampaign(v)}
                        disabled={endingId === v.id}
                        data-testid={`video-end-${v.id}`}
                        className="h-8 border-amber-200 text-amber-700 hover:bg-amber-50"
                        title="Encerrar (vai pro histórico)">
                  {endingId === v.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <StopCircle className="w-3.5 h-3.5" />}
                </Button>
              </>
            )}
            <Button size="sm" variant="outline"
                    onClick={() => remove(v)}
                    disabled={deletingId === v.id}
                    data-testid={`video-delete-${v.id}`}
                    className="h-8 border-red-200 text-red-600 hover:bg-red-50"
                    title="Excluir definitivamente">
              {deletingId === v.id
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Trash2 className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
        {isPreviewing && (
          <div className="mt-3 rounded overflow-hidden border border-slate-300 bg-slate-900">
            <video src={`${API}/admin/videos/${v.id}/stream`}
                    controls playsInline
                    className="w-full max-h-96"
                    data-testid={`video-player-${v.id}`} />
          </div>
        )}
      </div>
    );
  };

  const runningVideo = items.find((v) => v.computed_status === "running");

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center font-heading text-base">
              <Film className="w-4 h-4 mr-2 text-indigo-600" />
              Vídeos de Comunicação
            </CardTitle>
            <CardDescription className="text-xs">
              Vídeo em tela cheia no login — funcionário precisa assistir 100% e dar &quot;Ciente&quot;
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setUploadOpen(true)}
                   className="bg-indigo-600 hover:bg-indigo-700 text-white"
                   data-testid="video-open-upload">
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Novo Vídeo
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onCreated={load} />
        <AcksDialog open={!!acksVideo} video={acksVideo}
                     onClose={() => setAcksVideo(null)} />

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8">
            <Film className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Nenhum vídeo publicado ainda.</p>
            <p className="text-xs text-slate-400 mt-0.5">
              Clique em &quot;Novo Vídeo&quot; pra subir o primeiro.
            </p>
          </div>
        ) : (
          <>
            {runningVideo && (
              <div className="mb-3 p-2.5 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
                <b>No ar:</b> {runningVideo.title || runningVideo.filename}
                {" — "}
                <b>{runningVideo.ack_count}</b> de {totalEmployees}{" "}
                funcionário(s) deram Ciente{" "}
                (<b>{runningVideo.ack_percent}%</b>)
              </div>
            )}

            {active.length > 0 && (
              <div className="space-y-2 mb-4" data-testid="video-list-active">
                {active.map(renderItem)}
              </div>
            )}
            {active.length === 0 && (
              <p className="text-xs text-slate-500 italic mb-4">
                Nenhum vídeo ativo no momento — publique um novo.
              </p>
            )}

            {history.length > 0 && (
              <div className="border-t border-slate-200 pt-3">
                <button type="button"
                         onClick={() => setShowHistory((s) => !s)}
                         data-testid="video-toggle-history"
                         className="flex items-center gap-2 text-xs font-semibold text-slate-700 hover:text-indigo-700 mb-2">
                  <History className="w-4 h-4" />
                  Histórico ({history.length})
                  <span className="text-slate-400 font-normal">
                    {showHistory ? "▲" : "▼"}
                  </span>
                </button>
                {showHistory && (
                  <div className="space-y-2 mt-2" data-testid="video-list-history">
                    {history.map(renderItem)}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
